import { Component, ElementRef, Inject, OnDestroy, OnInit, Optional, ViewChild } from '@angular/core';
import { LocationCardData } from '../../models/location-card.model';
import {
    MatLegacyDialogRef as MatDialogRef,
    MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';
import { Store } from '@ngrx/store';
import { DynamicFormBuilderService } from '../../store/core/form-builder.service';
import { NormalizedFormState } from '../../store/config.models';
import { FormGroup } from '@angular/forms';
import { first, Observable } from 'rxjs';
import * as fromSelectors from '../../store/selectors';
import {
    handleConfirmationResult,
    initState,
    loadCustomProperties,
    loadLocations,
    loadModemProviders,
    loadQFinalEntities,
    loadRainGauges,
    resetForm,
    setFormData,
    setFromHomeDashboard,
} from '../../store/actions';
import { ConfirmationInput } from '../../store/reducers';
import { GISService } from 'app/shared/services/gis-service';
import { gisUserSettings } from 'app/shared/models/gis-service-list';
import { LocationCardService } from '../../services/location-card.service';
import { Router } from '@angular/router';
import { UiService } from 'app/shared/services/ui.service';
import { ValidatorRegistryService } from '../../store/core/validator-registry.service';
import { LocationService } from 'app/shared/services/location.service';
import { UsersService } from 'app/pages/admin/users.service';

@Component({
    selector: 'app-location-card-container-dialog',
    templateUrl: './location-card-container.component.html',
    styleUrls: ['./location-card-container.component.scss'],
})
export class LocationCardContainerDialogComponent implements OnInit, OnDestroy {
    @ViewChild('dialog') dialogDiv: ElementRef<HTMLDivElement>;

    public data: LocationCardData;
    public normalizedFormState$: Observable<NormalizedFormState>;
    public form$: Observable<FormGroup>;

    public title$: Observable<string>;
    public showConfirmation$: Observable<boolean>;
    public confirmationData$: Observable<ConfirmationInput>;
    public canOpenDialog = true;
    constructor(
        public dialogRef: MatDialogRef<LocationCardContainerDialogComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) public inputData: LocationCardData,
        private dynamicFormBuilderService: DynamicFormBuilderService,
        private store: Store,
        private gisService: GISService,
        private locationCardService: LocationCardService,
        private router: Router,
        private uiService: UiService,
        private validatorRegistry: ValidatorRegistryService,
        private locationService: LocationService,
        private usersService: UsersService,
    ) {
        this.data = inputData;
    }

    ngOnInit(): void {
        this.dialogRef.afterOpened().subscribe(() => this.uiService.disable());
        this.dialogRef.afterClosed().subscribe(() => this.uiService.enable());

        const isEditMode = this.data.editMode && !!this.data.locationDetails?.locationID;
        if (!isEditMode && !this.usersService.isMonitorEditor.getValue()) {
            this.canOpenDialog = false;
            this.dialogRef.close(false);
            return;
        }

        this.store.dispatch(resetForm());
        this.form$ = this.dynamicFormBuilderService.getFormFromStore();
        this.normalizedFormState$ = this.store.select(fromSelectors.selectFormState);

        this.showConfirmation$ = this.store.select(fromSelectors.selectShowConfirmation);
        this.confirmationData$ = this.store.select(fromSelectors.selectConfirmationData);
        this.title$ = this.store.select(fromSelectors.selectDialogTitle);


        const url = this.router.url;
        const isFromHomePage = url.indexOf('dashboard') > -1;

        this.store.dispatch(setFromHomeDashboard({ fromHomeDashboard: isFromHomePage }));

        this.store.dispatch(setFormData({ data: this.data }));
        this.store.dispatch(loadRainGauges({ data: this.data.rainGaugeLocations ?? [] }));
        this.store.dispatch(loadModemProviders());
        this.store.dispatch(loadCustomProperties({ customerId: this.data.customerId }));
        this.store.dispatch(
            loadQFinalEntities({
                customerId: this.data.customerId,
                locationId:
                    this.data.locationDetails && this.data.locationDetails.locationID
                        ? this.data.locationDetails.locationID
                        : 0,
            }),
        );
        this.store.dispatch(loadLocations({ customerId: this.data.customerId }));
        this.store.dispatch(initState({ data: this.data }));
    }

    handleConfirmationDialogResult(result: { whichButtonPressed: 'ok' | 'cancel' }) {
        this.store.dispatch(handleConfirmationResult({ result: result.whichButtonPressed === 'ok' }));
    }

    public dragEvent() {
        const { x, y } = this.dialogDiv.nativeElement.getClientRects()[0];

        const locationPositionX = String(x);
        const locationPositionY = String(y);
        this.gisService.gisUserSettingsSubject$.pipe(first()).subscribe((currentSettings: gisUserSettings) => {
            this.gisService
                .updateGisUserSettings({ ...currentSettings, locationPositionX, locationPositionY })
                .subscribe(() => {
                    this.gisService.locationCardPosition = this.locationCardService.getLocationCardPosition(true, {
                        locationPositionX,
                        locationPositionY,
                    });
                });
        });
    }

    ngOnDestroy(): void {
        this.dynamicFormBuilderService.unsubscribeAll();
        this.locationService.bustCache();
        this.validatorRegistry.asyncValidatorCache.clear();
    }
}
